import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as RKeyboardEvent } from "react";
import {
  EMAIL_FILTERS, emailKindInfo, expectsReply, filterEmails, forwardedDetails, noiseToArchive, outcomeLabel, previewText, readTimeLabel,
  replyAllLabel, respondByLabel, sortEmails, toneLabel, unreadCount, type EmailFilter, type EmailSort, type Tone,
} from "../email";
import { dayName, formatDate, repeatMarker } from "../format";
import { channelInfo, filterInbox, groupByWeek, inboxSenders, keyInbox, NO_SENDER } from "../inbox";
import { Portrait } from "../portrait/Portrait";
import type { EventView, InboxView, ObservedView, OptionView, PlayerAction } from "../types";
import { CostPreview } from "./CostPreview";
import { Modal } from "./Dialogs";
import { ForwardForm } from "./Forward";
import { Parts, PersonRef } from "./PersonRef";
import { TonePicker } from "./Reply";

type Tab = "inbox" | "sent";

/**
 * The inbox (21 §6, 19 §4.6): email waiting for you, and a record of everything dealt with.
 * Email never blocks time. Kinds and deadlines are described in words; nothing is rated, and
 * nothing ever says whether a reply was misread.
 */
export function InboxPanel({ view, busy, shortcuts, onAct, onActMany }: {
  view: ObservedView; busy: boolean; shortcuts: boolean;
  onAct: (a: PlayerAction) => Promise<ObservedView | undefined>;
  onActMany: (a: PlayerAction[]) => Promise<ObservedView | undefined>;
}) {
  const [tab, setTab] = useState<Tab>("inbox");
  const ids = useId();
  const unread = unreadCount(view.emails);
  const tabs: { id: Tab; label: string }[] = [
    { id: "inbox", label: unread > 0 ? `Inbox (${unread} unread)` : "Inbox" },
    { id: "sent", label: "Sent & dealt with" },
  ];
  const onTabKey = (e: RKeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const next: Tab = tab === "inbox" ? "sent" : "inbox";
    setTab(next);
    document.getElementById(`${ids}-tab-${next}`)?.focus();
  };
  return (
    <section className="panel" aria-labelledby="inbox-title">
      <h2 id="inbox-title" className="panel-title">Inbox</h2>
      <div className="tabs" role="tablist" aria-label="Inbox views">
        {tabs.map((t) => (
          <button key={t.id} id={`${ids}-tab-${t.id}`} type="button" role="tab" className="tabs__tab"
            aria-selected={tab === t.id} aria-controls={`${ids}-panel-${t.id}`} tabIndex={tab === t.id ? 0 : -1}
            onClick={() => setTab(t.id)} onKeyDown={onTabKey}>
            {t.label}
          </button>
        ))}
      </div>
      <div id={`${ids}-panel-${tab}`} role="tabpanel" aria-labelledby={`${ids}-tab-${tab}`} className="tabs__panel">
        {tab === "inbox"
          ? <EmailInbox view={view} busy={busy} shortcuts={shortcuts} onAct={onAct} onActMany={onActMany} />
          : <History inbox={view.inbox} />}
      </div>
    </section>
  );
}

/* --- waiting email ----------------------------------------------------------------------------- */

function EmailInbox({ view, busy, shortcuts, onAct, onActMany }: {
  view: ObservedView; busy: boolean; shortcuts: boolean;
  onAct: (a: PlayerAction) => Promise<ObservedView | undefined>;
  onActMany: (a: PlayerAction[]) => Promise<ObservedView | undefined>;
}) {
  const [filter, setFilter] = useState<EmailFilter>("all");
  const [sort, setSort] = useState<EmailSort>("newest");
  const [selected, setSelected] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const ids = useId();
  const emails = view.emails;
  const shown = useMemo(() => sortEmails(filterEmails(emails, filter), sort), [emails, filter, sort]);
  const current = emails.find((e) => e.instanceId === selected) ?? null;
  const noise = noiseToArchive(emails);
  const list = useRef<HTMLUListElement>(null);
  const shownId = useRef<string | null>(null);

  // When the reading pane closes (closed, answered or filed), put focus back on that email's row, or the next one,
  // rather than losing it (WCAG 2.4.3).
  useEffect(() => {
    const prev = shownId.current;
    shownId.current = current?.instanceId ?? null;
    if (!prev || current) return;
    const rows = list.current ? [...list.current.querySelectorAll<HTMLButtonElement>(".email-row__btn")] : [];
    const target = rows.find((b) => b.dataset.id === prev) ?? rows[0];
    if (target) target.focus();
    else {
      const h = document.getElementById("inbox-title");
      if (h) { h.tabIndex = -1; h.focus(); }
    }
  }, [current]);

  // An email that has been answered or filed leaves the inbox; close the pane with it.
  useEffect(() => {
    if (selected && !emails.some((e) => e.instanceId === selected)) setSelected(null);
  }, [emails, selected]);

  const open = (ev: EventView) => {
    setSelected(ev.instanceId);
    setStatus("");
    // Reading costs a few minutes, once.
    if (ev.email && !ev.email.read) void onAct({ type: "read", instanceId: ev.instanceId });
  };

  const archiveNoise = async () => {
    const n = noise.length;
    const next = await onActMany(noise.map((instanceId) => ({ type: "file" as const, instanceId })));
    if (next) setStatus(`Archived ${n} noise email${n === 1 ? "" : "s"}.`);
  };

  if (emails.length === 0) {
    return (
      <>
        <p className="panel-note">No email is waiting. New email arrives through the week and waits here; it never stops the clock.</p>
        <p className="visually-hidden" role="status" aria-live="polite">{status}</p>
        {status && <p className="muted" aria-hidden="true">{status}</p>}
      </>
    );
  }

  return (
    <div className="email">
      <div className="email__tools">
        <div className="filter-chips" role="group" aria-label="Show">
          {EMAIL_FILTERS.map((f) => (
            <button key={f.id} type="button" className="chip" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
        <label className="email__sort">
          <span>Sort</span>
          <select value={sort} onChange={(e) => setSort(e.target.value as EmailSort)}>
            <option value="newest">Newest first</option>
            <option value="reply_by">Reply-by first</option>
          </select>
        </label>
        {noise.length > 0 && (
          <button type="button" className="btn" disabled={busy} onClick={() => void archiveNoise()}>
            Archive all noise ({noise.length})
          </button>
        )}
      </div>
      <p className="inbox-count" role="status" aria-live="polite">
        {status || `${shown.length} of ${emails.length} email${emails.length === 1 ? "" : "s"} · ${unreadCount(emails)} unread`}
      </p>
      <div className="email__layout">
        <ul className="email-list" aria-label="Email waiting" ref={list}>
          {shown.length === 0 && <li className="muted email-list__none">Nothing matches.</li>}
          {shown.map((ev) => {
            const e = ev.email;
            const kind = emailKindInfo(e?.kind);
            const unreadNow = !!e && !e.read;
            const due = respondByLabel(e?.respondBy, view.clock);
            return (
              <li key={ev.instanceId} className={`email-row${unreadNow ? " email-row--unread" : ""}`}>
                <button type="button" className="email-row__btn" data-id={ev.instanceId} aria-current={selected === ev.instanceId ? "true" : undefined}
                  aria-describedby={`${ids}-${ev.instanceId}-meta`} onClick={() => open(ev)}>
                  <span className="email-row__dot" aria-hidden="true">{unreadNow ? "●" : ""}</span>
                  <span className="email-row__main">
                    <span className="email-row__who">
                      {unreadNow && <span className="visually-hidden">Unread. </span>}
                      {ev.bringer ? <PersonRef personId={ev.bringer.id} person={ev.bringer} passive>{ev.bringer.name}</PersonRef> : "No named sender"}
                    </span>
                    <span className="email-row__subject">{ev.title}</span>
                    <span className="email-row__preview">{previewText(ev.text)}</span>
                  </span>
                  <span className="email-row__meta" id={`${ids}-${ev.instanceId}-meta`}>
                    <span className={`kind kind--${e?.kind ?? "email"}`}><span aria-hidden="true">{kind.glyph} </span>{kind.word}</span>
                    {due && <span className="email-row__due">{due}</span>}
                    {unreadNow && <span className="email-row__read">{readTimeLabel(e?.readMinutes)}</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        <div className="email__pane">
          {current
            ? <ReadingPane key={current.instanceId} ev={current} view={view} busy={busy} shortcuts={shortcuts}
                onRead={() => void onAct({ type: "read", instanceId: current.instanceId })}
                onDone={(msg, a) => { void onAct(a).then((next) => { if (next) setStatus(msg); }); }}
                onClose={() => setSelected(null)} />
            : <p className="email__empty muted">Choose an email to read it. Opening an unread email takes a few minutes of your time.</p>}
        </div>
      </div>
    </div>
  );
}

function ReadingPane({ ev, view, busy, shortcuts, onRead, onDone, onClose }: {
  ev: EventView; view: ObservedView; busy: boolean; shortcuts: boolean;
  onRead: () => void; onDone: (status: string, a: PlayerAction) => void; onClose: () => void;
}) {
  const [writing, setWriting] = useState<OptionView | null>(null);
  const [confirmFile, setConfirmFile] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const options = useRef<HTMLOListElement>(null);
  const wasWriting = useRef(false);
  const forwardBtn = useRef<HTMLButtonElement>(null);
  const wasForwarding = useRef(false);
  const e = ev.email;
  const kind = emailKindInfo(e?.kind);
  const due = respondByLabel(e?.respondBy, view.clock);
  // Scroll the heading into view too: below 1180 px the pane sits under the list.
  useEffect(() => { heading.current?.focus(); }, [ev.instanceId]);
  // Back from choosing a tone: focus returns to the replies.
  useEffect(() => {
    if (wasWriting.current && !writing) options.current?.querySelector<HTMLButtonElement>("button")?.focus();
    wasWriting.current = writing !== null;
  }, [writing]);
  // Back from the forward form: focus returns to the button that opened it (WCAG 2.4.3).
  useEffect(() => {
    if (wasForwarding.current && !forwarding) forwardBtn.current?.focus();
    wasForwarding.current = forwarding;
  }, [forwarding]);
  const cc = e?.cc ?? [];

  const send = (o: OptionView, tone?: Tone) =>
    onDone("Sent.", { type: "choose", instanceId: ev.instanceId, optionId: o.id, ...(tone ? { tone } : {}) });
  const file = () => onDone("Filed without replying.", { type: "file", instanceId: ev.instanceId });
  const fileClicked = () => { if (expectsReply(e?.kind)) setConfirmFile(true); else file(); };

  return (
    <article className="reading" aria-labelledby="reading-title">
      <div className="reading__head">
        <h3 id="reading-title" ref={heading} tabIndex={-1} className="reading__title">{ev.title}</h3>
        <button type="button" className="btn btn--icon btn--quiet" onClick={onClose} aria-label="Close this email">×</button>
      </div>
      <p className="reading__from">
        From {ev.bringer
          ? <><PersonRef personId={ev.bringer.id} person={ev.bringer}>{ev.bringer.name}</PersonRef><span className="muted">, {ev.bringer.role}</span></>
          : "no named sender"}
      </p>
      <p className="reading__meta">
        <span className={`kind kind--${e?.kind ?? "email"}`}><span aria-hidden="true">{kind.glyph} </span>{kind.word}</span>
        {e?.sentAt && <span>Sent {e.sentAt}</span>}
        {due && <span className="email-row__due">{due}</span>}
      </p>
      {cc.length > 0 && (
        <p className="reading__cc">
          <span className="reading__cc-label">Cc:</span>{" "}
          {cc.map((p, i) => (
            <span key={p.id}>
              {i > 0 && (i === cc.length - 1 ? " and " : ", ")}
              <PersonRef personId={p.id} person={p}>{p.name}</PersonRef>
            </span>
          ))}
        </p>
      )}
      {e && !e.read ? (
        <div className="reading__unread">
          <p className="muted">{busy ? "Opening…" : "Not opened yet."} {readTimeLabel(e.readMinutes)}.</p>
          {!busy && <button type="button" className="btn" onClick={onRead}>Read it</button>}
        </div>
      ) : (
        <>
          <blockquote className="dialogue reading__body"><Parts parts={ev.textParts} text={ev.text} /></blockquote>
          {e?.hints.length ? <p className="reading__hint">You notice: {e.hints.join(" ")}</p> : null}
          {ev.repeat && <p className="repeat-marker">{repeatMarker(ev.repeat.count)}</p>}
          {writing ? (
            <TonePicker option={writing} busy={busy} shortcuts={shortcuts} cc={cc} onSend={(tone) => send(writing, tone)} onBack={() => setWriting(null)} />
          ) : forwarding ? (
            <ForwardForm instanceId={ev.instanceId} view={view} busy={busy} onSend={onDone} onCancel={() => setForwarding(false)} />
          ) : (
            <>
              <h4 className="reading__prompt" id="reading-prompt">What do you do?</h4>
              <ol className="options" aria-labelledby="reading-prompt" ref={options}>
                {ev.options.map((o) => {
                  const toAll = replyAllLabel(o, cc);
                  return (
                  <li key={o.id}>
                    <button type="button" className="option option--compact" disabled={busy}
                      onClick={() => (o.tones?.length ? setWriting(o) : send(o))}>
                      <span className="option__text">
                        {toAll && <span className="option__to-all"><span aria-hidden="true">⇉ </span>{toAll}: </span>}
                        <Parts parts={o.textParts} text={o.text} passive />
                        {o.tones?.length ? <span className="option__then"> Then choose how to put it.</span> : null}
                      </span>
                      <CostPreview cost={o.cost} />
                    </button>
                  </li>
                  );
                })}
              </ol>
              <div className="row-actions">
                <button type="button" className="btn" disabled={busy} ref={forwardBtn} onClick={() => setForwarding(true)}>Forward…</button>
                <button type="button" className="btn" disabled={busy} onClick={fileClicked}>File without replying</button>
              </div>
            </>
          )}
        </>
      )}
      <Modal title="File without replying?" open={confirmFile} onClose={() => setConfirmFile(false)}>
        <p>Filing it means not replying. People notice.</p>
        <div className="row-actions">
          <button type="button" className="btn" data-autofocus onClick={() => setConfirmFile(false)}>Keep it in the inbox</button>
          <button type="button" className="btn btn--primary" disabled={busy} onClick={() => { setConfirmFile(false); file(); }}>File it</button>
        </div>
      </Modal>
    </article>
  );
}

/* --- sent and dealt with ----------------------------------------------------------------------- */

/**
 * Everything that has reached you and how it was dealt with (19 §4.6), newest first, by week.
 * A record, not a to-do list: nothing here is marked urgent or rated.
 */
export function History({ inbox }: { inbox: InboxView[] }) {
  const [person, setPerson] = useState("");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const ids = useId();

  const entries = useMemo(() => keyInbox(inbox), [inbox]);
  const senders = useMemo(() => inboxSenders(inbox), [inbox]);
  const shown = useMemo(() => filterInbox(entries, person, query), [entries, person, query]);
  const weeks = useMemo(() => groupByWeek(shown), [shown]);
  const anonymous = inbox.some((m) => !m.from);
  const filtering = person !== "" || query.trim() !== "";

  const toggle = (key: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  if (inbox.length === 0) {
    return <p className="panel-note">Nothing has been dealt with yet. Messages, calls and conversations appear here once they have been.</p>;
  }
  return (
    <>
      <form className="inbox-filter" role="search" onSubmit={(e) => e.preventDefault()}>
        <label htmlFor={`${ids}-who`}>From</label>
        <select id={`${ids}-who`} value={person} onChange={(e) => setPerson(e.target.value)}>
          <option value="">Everyone</option>
          {senders.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          {anonymous && <option value={NO_SENDER}>No named sender</option>}
        </select>
        <label htmlFor={`${ids}-q`}>Search</label>
        <input id={`${ids}-q`} type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Words in the message or reply" />
      </form>
      <p className="inbox-count" role="status" aria-live="polite">
        {filtering ? `${shown.length} of ${inbox.length} messages` : `${inbox.length} message${inbox.length === 1 ? "" : "s"}`}
      </p>
      {weeks.length === 0 && <p className="muted">Nothing matches.</p>}
      {weeks.map((w) => (
        <div key={w.week} className="inbox-week">
          <h3 className="inbox-week__title">Week {w.week}</h3>
          <ul className="inbox-list">
            {w.entries.map(({ key, item }) => {
              const ch = channelInfo(item.channel);
              const expanded = open.has(key);
              const bodyId = `${ids}-${key}`;
              const when = `${dayName(item.day).slice(0, 3)} ${formatDate(item.date)}`;
              const outcome = outcomeLabel(item);
              const tone = toneLabel(item.tone);
              return (
                <li key={key} className="inbox-item">
                  <button type="button" className="inbox-item__head" aria-expanded={expanded} aria-controls={bodyId}
                    onClick={() => toggle(key)}>
                    {item.from ? (
                      <Portrait portraitId={item.from.portraitId} seed={item.from.id} name={item.from.name} size={36} decorative />
                    ) : (
                      <span className="inbox-item__nobody" aria-hidden="true">{ch.glyph}</span>
                    )}
                    <span className="inbox-item__main">
                      <span className="inbox-item__who">
                        {item.from ? <PersonRef personId={item.from.id} person={item.from} passive>{item.from.name}</PersonRef> : "No named sender"}
                        {item.from && <span className="inbox-item__role">, {item.from.role}</span>}
                      </span>
                      <span className="inbox-item__title">{item.title}</span>
                    </span>
                    <span className="inbox-item__meta">
                      <span className="inbox-item__channel"><span aria-hidden="true">{ch.glyph}</span> {ch.word}</span>
                      <span className="num">{when}</span>
                      {outcome
                        ? <span className="inbox-item__outcome">{outcome}{tone && item.outcome === "replied" ? ` · ${tone}` : ""}</span>
                        : item.auto && <span className="inbox-item__auto">Left waiting</span>}
                    </span>
                    <span className="inbox-item__chev" aria-hidden="true">{expanded ? "▾" : "▸"}</span>
                  </button>
                  <div id={bodyId} className="inbox-item__body" hidden={!expanded}>
                    {item.from && (
                      <p className="inbox-item__from">From <PersonRef personId={item.from.id} person={item.from}>{item.from.name}</PersonRef></p>
                    )}
                    <blockquote className="dialogue">{item.text}</blockquote>
                    <HistoryReply item={item} tone={tone} />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      {inbox.length >= 150 && <p className="panel-note">Only the most recent 150 messages are kept here.</p>}
    </>
  );
}

function HistoryReply({ item, tone }: { item: InboxView; tone: string | null }) {
  if (item.outcome === "filed") return <p className="inbox-item__reply"><strong>Filed without replying.</strong></p>;
  if (item.outcome === "no_reply") return <p className="inbox-item__reply"><strong>No reply was sent.</strong></p>;
  const fwd = forwardedDetails(item);
  if (fwd) {
    return (
      <p className="inbox-item__reply">
        <strong>Forwarded to {fwd.to}{fwd.note ? " with a note:" : ", without a note."}</strong>{fwd.note && <> <q>{fwd.note}</q></>}
      </p>
    );
  }
  if (item.auto) {
    return (
      <p className="inbox-item__reply">
        <strong>Left waiting — handled the usual way:</strong> <q>{item.reply}</q>
      </p>
    );
  }
  return (
    <p className="inbox-item__reply">
      <strong>You replied{tone ? ` (${tone.toLowerCase()})` : ""}:</strong> <q>{item.reply}</q>
    </p>
  );
}
